import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Plus, Edit2, Trash2, GripVertical, FolderOpen, Package,
  Loader, Save, X, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MAX_CATEGORIES = 15;

export const SellerCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newName, setNewName] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/seller/categories`, { withCredentials: true });
      setCategories(response.data.categories || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Erro ao carregar categorias');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Digite um nome para a categoria');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/seller/categories`,
        { name: newName.trim() },
        { withCredentials: true }
      );
      toast.success('Categoria criada!');
      setNewName('');
      setIsDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar categoria');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!newName.trim() || !editingCategory) {
      toast.error('Digite um nome para a categoria');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/seller/categories/${editingCategory.category_id}`,
        { name: newName.trim() },
        { withCredentials: true }
      );
      toast.success('Categoria atualizada!');
      setNewName('');
      setEditingCategory(null);
      setIsDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar categoria');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Excluir categoria "${category.name}"? Os produtos ficarão sem categoria.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/seller/categories/${category.category_id}`,
        { withCredentials: true }
      );
      toast.success('Categoria excluída!');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newCategories = [...categories];
    const draggedCategory = newCategories[draggedItem];
    newCategories.splice(draggedItem, 1);
    newCategories.splice(index, 0, draggedCategory);

    setCategories(newCategories);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (draggedItem === null) return;

    setDraggedItem(null);

    // Salvar nova ordem no backend
    try {
      await axios.post(
        `${API_URL}/seller/categories/reorder`,
        { category_ids: categories.map(c => c.category_id) },
        { withCredentials: true }
      );
      toast.success('Ordem atualizada!');
    } catch (error) {
      toast.error('Erro ao salvar ordem');
      fetchCategories(); // Reverter
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setNewName(category.name);
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingCategory(null);
    setNewName('');
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="categories-title">
              Minhas Categorias
            </h1>
            <p className="text-lg text-gray-600">
              Organize seus produtos em categorias personalizadas
            </p>
          </div>
          <Button
            onClick={openCreateDialog}
            disabled={categories.length >= MAX_CATEGORIES}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="add-category-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Categoria
          </Button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Dicas de organização</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Arraste as categorias para reordenar</li>
                <li>Categorias aparecem na vitrine da sua loja</li>
                <li>Máximo de {MAX_CATEGORIES} categorias</li>
                <li>Produtos sem categoria aparecem em "Outros"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Counter */}
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm text-gray-600">
            {categories.length} de {MAX_CATEGORIES} categorias
          </span>
          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-indigo-600 transition-all"
              style={{ width: `${(categories.length / MAX_CATEGORIES) * 100}%` }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader className="w-12 h-12 animate-spin text-indigo-600" />
          </div>
        ) : categories.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhuma categoria ainda</h3>
            <p className="text-gray-600 mb-6">Crie categorias para organizar seus produtos!</p>
            <Button
              onClick={openCreateDialog}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeira Categoria
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {categories.map((category, index) => (
                <li
                  key={category.category_id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors cursor-move ${
                    draggedItem === index ? 'bg-indigo-50 border-2 border-indigo-300' : ''
                  }`}
                  data-testid={`category-item-${category.category_id}`}
                >
                  <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      <Package className="w-3 h-3 inline mr-1" />
                      {category.product_count || 0} produto(s)
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(category)}
                      data-testid={`edit-category-${category.category_id}`}
                    >
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(category)}
                      data-testid={`delete-category-${category.category_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Dialog de Criar/Editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="category-dialog-title">
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
            <DialogDescription>
              {editingCategory 
                ? 'Altere o nome da categoria'
                : 'Digite um nome para sua nova categoria'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div>
              <Label>Nome da Categoria</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: Camisetas, Acessórios, Promoções..."
                maxLength={50}
                data-testid="category-name-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {newName.length}/50 caracteres
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={editingCategory ? handleUpdate : handleCreate}
                disabled={saving || !newName.trim()}
                className="flex-1"
                data-testid="category-submit-btn"
              >
                {saving ? (
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editingCategory ? 'Salvar' : 'Criar'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                data-testid="category-cancel-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
